<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useContactLabelsStore } from 'dashboard/stores/contactLabels';

import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  selectedLabels: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['update:selectedLabels']);

const { t } = useI18n();
const contactLabelsStore = useContactLabelsStore();

const labelSearch = ref('');
const showLabelDropdown = ref(false);
const isCreatingLabel = ref(false);

const allLabels = computed(() => contactLabelsStore.getLabels);

const labelColorMap = computed(() => contactLabelsStore.getLabelColorMap);

const filteredLabels = computed(() => {
  const q = labelSearch.value.toLowerCase();
  const current = new Set(props.selectedLabels);
  return allLabels.value.filter(
    l => !current.has(l.title) && l.title.toLowerCase().includes(q)
  );
});

const canCreateLabel = computed(() => {
  const q = labelSearch.value.trim().toLowerCase();
  if (!q) return false;
  return !allLabels.value.some(l => l.title.toLowerCase() === q);
});

const addLabel = title => {
  labelSearch.value = '';
  showLabelDropdown.value = false;
  emit('update:selectedLabels', [...props.selectedLabels, title]);
};

const createAndAddLabel = async () => {
  const title = labelSearch.value.trim();
  if (!title || isCreatingLabel.value || !canCreateLabel.value) return;
  isCreatingLabel.value = true;
  try {
    await contactLabelsStore.create({ title });
    addLabel(title);
  } finally {
    isCreatingLabel.value = false;
  }
};

const removeLabel = title => {
  emit(
    'update:selectedLabels',
    props.selectedLabels.filter(l => l !== title)
  );
};

onMounted(() => contactLabelsStore.fetchAll());
</script>

<template>
  <div class="flex flex-wrap items-center gap-1.5">
    <span
      v-for="label in selectedLabels"
      :key="label"
      class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-white"
      :style="{
        backgroundColor: labelColorMap[label] || '#6B7280',
      }"
    >
      {{ label }}
      <button
        type="button"
        class="ml-0.5 opacity-70 hover:opacity-100 transition-opacity"
        @click="removeLabel(label)"
      >
        <Icon icon="i-lucide-x" class="size-3" />
      </button>
    </span>

    <div class="relative">
      <button
        type="button"
        class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border border-dashed border-n-weak text-n-slate-11 hover:border-n-blue-8 hover:text-n-blue-11 transition-colors"
        @click="showLabelDropdown = !showLabelDropdown"
      >
        <Icon icon="i-lucide-plus" class="size-3" />
        {{ t('CONTACTS_LAYOUT.CARD.LABELS_FORM.ADD_LABELS') }}
      </button>
      <div
        v-if="showLabelDropdown"
        v-on-clickaway="() => (showLabelDropdown = false)"
        class="absolute left-0 top-7 z-50 bg-n-alpha-3 backdrop-blur-[100px] outline outline-1 outline-n-container rounded-xl shadow-lg w-48 flex flex-col overflow-hidden"
      >
        <div class="px-2 pt-2 pb-1">
          <input
            v-model="labelSearch"
            type="text"
            :placeholder="t('BULK_ACTION.SEARCH_INPUT_PLACEHOLDER')"
            class="reset-base w-full h-7 px-2 text-xs rounded-lg bg-n-alpha-black2 dark:bg-n-solid-1 text-n-slate-12 focus:outline-none"
            @click.stop
            @keydown.enter.prevent="createAndAddLabel"
          />
        </div>
        <div class="max-h-40 overflow-y-auto px-1 pb-1 flex flex-col gap-0.5">
          <button
            v-for="l in filteredLabels"
            :key="l.id"
            type="button"
            class="flex items-center gap-2 w-full px-2 py-1 text-sm rounded-lg text-n-slate-12 hover:bg-n-alpha-1 text-left"
            @click="addLabel(l.title)"
          >
            <span
              class="h-2.5 w-2.5 rounded-sm shrink-0"
              :style="{ backgroundColor: l.color }"
            />
            {{ l.title }}
          </button>
          <button
            v-if="canCreateLabel"
            type="button"
            class="flex items-center gap-2 w-full px-2 py-1 text-sm rounded-lg text-n-slate-12 hover:bg-n-alpha-1 text-left"
            @click="createAndAddLabel"
          >
            <Icon icon="i-lucide-plus" class="size-3.5 shrink-0" />
            {{
              t('CONTACTS_LAYOUT.CARD.LABELS_FORM.CREATE_LABEL', {
                title: labelSearch.trim(),
              })
            }}
          </button>
          <span
            v-if="!filteredLabels.length && !canCreateLabel"
            class="text-xs text-n-slate-9 px-2 py-1"
          >
            {{ t('CONTACTS_LAYOUT.CARD.LABELS_FORM.EMPTY') }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
